import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { 
  CreditCard, ExternalLink, CheckCircle2, AlertCircle, 
  Loader2, ArrowRight, ShieldCheck, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { getApiErrorMessage } from '../../utils/errorHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function StripeConnect() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboarding, setOnboarding] = useState(false);
  const [onlinePaymentEnabled, setOnlinePaymentEnabled] = useState(true);
  const [togglingPayment, setTogglingPayment] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/stripe-connect/status`);
      setStatus(res.data);
      if (res.data.online_payment_enabled !== undefined) {
        setOnlinePaymentEnabled(res.data.online_payment_enabled);
      }
    } catch {
      toast.error('Erreur lors de la vérification du statut Stripe');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleToggleOnlinePayment = async () => {
    setTogglingPayment(true);
    try {
      const res = await axios.post(`${API_URL}/api/stripe-connect/toggle-online-payment`);
      setOnlinePaymentEnabled(res.data.online_payment_enabled);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors du changement'));
    } finally {
      setTogglingPayment(false);
    }
  };

  // Handle return from Stripe onboarding
  useEffect(() => {
    const stripeParam = searchParams.get('stripe');
    if (stripeParam === 'return') {
      toast.success('Retour de Stripe. Vérification du statut...');
      fetchStatus();
    } else if (stripeParam === 'refresh') {
      toast.info('Session expirée. Veuillez recommencer l\'onboarding.');
    }
  }, [searchParams, fetchStatus]);

  const handleStartOnboarding = async () => {
    setOnboarding(true);
    try {
      const res = await axios.post(`${API_URL}/api/stripe-connect/onboard`, {
        origin_url: window.location.origin
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Erreur lors de la création du compte'));
      setOnboarding(false);
    }
  };

  const handleOpenDashboard = async () => {
    // Open window immediately on user click to avoid popup blockers
    const newWindow = window.open('about:blank', '_blank');
    try {
      const res = await axios.post(`${API_URL}/api/stripe-connect/dashboard-link`, {
        origin_url: window.location.origin
      });
      if (newWindow) {
        newWindow.location.href = res.data.url;
      } else {
        // Fallback if popup was still blocked
        window.location.href = res.data.url;
      }
    } catch (err) {
      if (newWindow) newWindow.close();
      toast.error(getApiErrorMessage(err, "Erreur d'acces au tableau de bord Stripe"));
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </DashboardLayout>
    );
  }

  const isFullyActive = status?.charges_enabled && status?.payouts_enabled;
  const isPending = status?.connected && !isFullyActive;
  const isNotConnected = !status?.connected;

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="stripe-connect-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Paiements Stripe Connect</h1>
          <p className="text-gray-500 mt-1">
            Recevez les paiements de vos clients directement sur votre compte bancaire.
          </p>
        </div>

        {/* Status Card */}
        <div className={`rounded-2xl border p-6 ${
          isFullyActive ? 'bg-emerald-50 border-emerald-200' :
          isPending ? 'bg-amber-50 border-amber-200' :
          'bg-gray-50 border-gray-200'
        }`} data-testid="stripe-connect-status">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isFullyActive ? 'bg-emerald-100' :
              isPending ? 'bg-amber-100' :
              'bg-gray-100'
            }`}>
              {isFullyActive ? (
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              ) : isPending ? (
                <AlertCircle className="w-6 h-6 text-amber-600" />
              ) : (
                <CreditCard className="w-6 h-6 text-gray-600" />
              )}
            </div>
            <div className="flex-1">
              <h2 className="font-semibold text-gray-900 text-lg">
                {isFullyActive ? 'Compte Stripe actif' :
                 isPending ? 'Onboarding en cours' :
                 'Compte Stripe non connecté'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {isFullyActive 
                  ? 'Votre compte est vérifié. Vous recevez les paiements directement.'
                  : isPending
                  ? 'Votre compte nécessite des vérifications supplémentaires. Complétez l\'onboarding pour activer les paiements.'
                  : 'Connectez votre compte Stripe pour recevoir les paiements en ligne de vos clients.'}
              </p>

              {/* Status details for pending/active */}
              {status?.connected && (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    {status.charges_enabled ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="text-sm text-gray-700">
                      Paiements {status.charges_enabled ? 'activés' : 'en attente'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {status.payouts_enabled ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    )}
                    <span className="text-sm text-gray-700">
                      Virements {status.payouts_enabled ? 'activés' : 'en attente'}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-3">
            {isNotConnected && (
              <Button
                onClick={handleStartOnboarding}
                disabled={onboarding}
                className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                data-testid="start-onboarding-btn"
              >
                {onboarding ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Connecter mon compte Stripe
              </Button>
            )}
            {isPending && (
              <>
                <Button
                  onClick={handleStartOnboarding}
                  disabled={onboarding}
                  className="bg-amber-500 hover:bg-amber-600 rounded-xl"
                  data-testid="continue-onboarding-btn"
                >
                  {onboarding ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Continuer l'onboarding
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchStatus}
                  className="rounded-xl"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser le statut
                </Button>
              </>
            )}
            {isFullyActive && (
              <>
                <Button
                  onClick={handleOpenDashboard}
                  className="bg-emerald-500 hover:bg-emerald-600 rounded-xl"
                  data-testid="open-stripe-dashboard-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Tableau de bord Stripe
                </Button>
                <Button
                  variant="outline"
                  onClick={fetchStatus}
                  className="rounded-xl"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Actualiser
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Online Payment Toggle — only show when Stripe is connected */}
        {status?.charges_enabled && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="online-payment-toggle-card">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Paiement en ligne</h3>
                <p className="text-sm text-gray-500 mt-1">
                  {onlinePaymentEnabled
                    ? "Les clients peuvent payer en ligne par carte bancaire via Stripe."
                    : "Le paiement en ligne est desactive. Les clients ne pourront payer que sur place."}
                </p>
              </div>
              <button
                onClick={handleToggleOnlinePayment}
                disabled={togglingPayment}
                data-testid="online-payment-toggle"
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                  onlinePaymentEnabled ? 'bg-emerald-500' : 'bg-gray-300'
                } ${togglingPayment ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    onlinePaymentEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {!onlinePaymentEnabled && (
              <div className="mt-3 flex items-start gap-2 p-3 bg-amber-50 rounded-xl border border-amber-200">
                <CreditCard className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700">
                  Vos clients verront que le paiement en ligne n'est pas disponible et devront payer sur place a la recuperation de leur commande.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Info Card */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
          <h3 className="font-semibold text-gray-900">Comment ça marche ?</h3>
          <div className="space-y-3">
            {[
              {
                title: 'Paiement sécurisé',
                desc: 'Les clients paient en ligne via Stripe. Le montant est directement versé sur votre compte.',
              },
              {
                title: 'Commission de 5%',
                desc: 'LEMO prélève automatiquement 5% de commission sur chaque paiement en ligne.',
              },
              {
                title: 'Virements automatiques',
                desc: 'Stripe transfère vos revenus directement sur votre compte bancaire selon votre calendrier.',
              },
            ].map((item) => (
              <div key={item.title} className="flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
